import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom, take } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { FieldInterventionListItemComponent } from '@connect-services/client/components/field-intervention-list/field-intervention-list-item/field-intervention-list-item.component';
import { MainTitleComponent } from '@connect-services/client/components/main-title/main-title.component';
import { CreateEditFieldInterventionModalComponent } from '@connect-services/client/components/modals/create-edit-field-intervention-modal/create-edit-field-intervention-modal.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import {
    FieldInterventionCreateEditModalData,
    FieldInterventionCreationEditionForm,
    FieldInterventionModalCloseModeEnum,
    FieldInterventionModalResult,
} from '@connect-services/client/models/create-edit-field-intervention-modal.model';
import { ActionsEnum, FieldInterventionEnum } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    FieldIntervention,
    FieldInterventionControllerGet200Response,
    FieldInterventionsService,
    MultiPolygon,
} from '@connect-services/client/sdk/api';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import {
    FieldInterventionAreaSelectionContextEnum,
    FieldInterventionAreaSelectionModeService,
} from '@connect-services/client/services/field-intervention-area-selection-mode.service';
import { SelectionService } from '@connect-services/client/services/map/selection/selection.service';
import { MapControlService } from '@connect-services/client/services/map-control.service';
import { MapDrawService } from '@connect-services/client/services/map-draw.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';
import { PanelService } from '@connect-services/client/services/panel.service';
import { SelectZoneLayerService } from '@connect-services/client/services/select-zone-layer.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { defaultPaginationParameters, OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';

export type InterventionsByStatus = { fieldInterventions: FieldIntervention[]; name: FieldIntervention.StatusEnum };

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        FieldInterventionListItemComponent,
        MainTitleComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
    ],
    selector: 'app-field-intervention-list',
    styleUrls: ['./field-intervention-list.component.scss'],
    templateUrl: './field-intervention-list.component.html',
})
export class FieldInterventionListComponent {
    public readonly fieldInterventions: WritableSignal<FieldIntervention[]> = signal([]);
    public readonly fieldInterventionsByStatus = computed(() =>
        this.splitFieldInterventionsByStatus(this.fieldInterventions(), this.searchValue()),
    );
    public readonly searchValue = signal('');

    protected readonly ActionsEnum = ActionsEnum;
    protected readonly ButtonType = ButtonType;

    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    private readonly fieldInterventionAreaSelectionModeService = inject(FieldInterventionAreaSelectionModeService);
    private readonly fieldInterventionsService = inject(FieldInterventionsService);
    private readonly mapControlService = inject(MapControlService);
    private readonly mapDrawService = inject(MapDrawService);
    private readonly mapModalService = inject(MapModalService);
    private readonly matDialog = inject(MatDialog);
    private readonly panelService = inject(PanelService);
    private pendingFieldInterventionModalData: FieldInterventionCreateEditModalData | null = null;
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);
    private readonly selectionService = inject(SelectionService);
    private readonly selectZoneLayerService = inject(SelectZoneLayerService);
    private readonly sourcesService = inject(SourcesService);

    public constructor() {
        effect(async () => {
            this.searchValue.set('');

            if (!this.projectBloc.value()) {
                return;
            }

            await this.fetchAllFieldInterventions();
        });
    }

    public openNewFieldInterventionModal(data?: FieldInterventionCreateEditModalData): void {
        const dialogRef = this.matDialog.open(CreateEditFieldInterventionModalComponent, {
            autoFocus: false,
            data: data ?? { title: 'createNewFieldIntervention' },
            width: '60rem',
        });

        dialogRef.afterClosed().subscribe((result: FieldInterventionModalResult | null) => {
            void this.handleFieldInterventionModalResult(result);
        });
    }

    private buildPendingFieldInterventionModalData(
        creationEditionForm: FieldInterventionCreationEditionForm,
    ): FieldInterventionCreateEditModalData {
        return {
            description: creationEditionForm.controls.description.value ?? undefined,
            fieldInterventionArea: creationEditionForm.controls.fieldInterventionArea.value,
            name: creationEditionForm.controls.name.value,
            scheduledEndDate: creationEditionForm.controls.scheduledEndDate.value ?? undefined,
            title: 'createNewFieldIntervention',
        };
    }

    private async cancelFieldInterventionCreation(): Promise<void> {
        await this.closeFieldInterventionAreaSelectionMode();
        this.cleanPendingFieldInterventionAreaSelection();
        this.restoreFieldInterventionPanel();
    }

    private cleanPendingFieldInterventionAreaSelection(): void {
        this.mapDrawService.resetDraw();
        this.pendingFieldInterventionModalData = null;
    }

    private async closeFieldInterventionAreaSelectionMode(): Promise<void> {
        this.mapModalService.close(MapModal.SELECTION);
        this.mapModalService.close(MapModal.FILTER);

        this.mapControlService.hideSelectionModalControl();

        this.mapDrawService.resetDraw();
        await this.selectZoneLayerService.removeLayers();

        this.selectionService.resetSelectionState();
    }

    private async createFieldInterventionFromModalResult(result: FieldInterventionModalResult): Promise<void> {
        const creationEditionForm: FieldInterventionCreationEditionForm | undefined = result.creationEditionForm;
        if (!creationEditionForm?.value.name) {
            return;
        }

        const fieldIntervention =
            await this.createFieldInterventionService.createFieldIntervention(creationEditionForm);

        await this.router.navigate([
            RoutePathEnum.PROJECTS,
            this.projectBloc.value()!.id,
            RoutePathEnum.FIELD_INTERVENTIONS,
            fieldIntervention.id,
            RoutePathEnum.FIELD_INTERVENTION_CREATE,
        ]);

        await this.closeFieldInterventionAreaSelectionMode();
        this.sourcesService.sources.set([]);
        this.cleanPendingFieldInterventionAreaSelection();
        this.openFieldInterventionEntitySelectionPanel();
    }

    private async fetchAllFieldInterventions(): Promise<void> {
        let hasNextPage = false;
        const paginationParameters: PaginationParameters = {
            ...defaultPaginationParameters,
            order: OrderEnum.ASC,
            orderBy: 'name',
        };

        const fieldInterventions: FieldIntervention[] = [];

        do {
            const paginatedFieldInterventions: FieldInterventionControllerGet200Response = await firstValueFrom(
                this.fieldInterventionsService.fieldInterventionControllerGet({
                    ...paginationParameters,
                    project: this.projectBloc.value()!.id,
                }),
            );

            fieldInterventions.push(...paginatedFieldInterventions.items);
            hasNextPage = paginatedFieldInterventions.hasNextPage;
            paginationParameters.page += 1;
        } while (hasNextPage);

        this.fieldInterventions.set(fieldInterventions);
    }

    private async handleFieldInterventionModalResult(result: FieldInterventionModalResult | null): Promise<void> {
        if (!result) {
            await this.cancelFieldInterventionCreation();

            return;
        }

        if (result.mode === FieldInterventionModalCloseModeEnum.FIELD_INTERVENTION_AREA_SELECTION) {
            this.startFieldInterventionAreaSelection(result.creationEditionForm);

            return;
        }

        await this.createFieldInterventionFromModalResult(result);
    }

    private listenFieldInterventionAreaSelectionConfirmation(): void {
        this.fieldInterventionAreaSelectionModeService.selectionConfirmed
            .pipe(take(1))
            .subscribe(async (fieldInterventionArea: MultiPolygon) => {
                this.setPendingFieldInterventionArea(fieldInterventionArea);
                await this.closeFieldInterventionAreaSelectionMode();
                this.openNewFieldInterventionModal(this.pendingFieldInterventionModalData ?? undefined);
            });
    }

    private openFieldInterventionEntitySelectionPanel(): void {
        this.panelService.closePanel();
        this.contentLayoutService.changeContentSize(FieldInterventionEnum.FIELD_INTERVENTION_CREATE);
    }

    private restoreFieldInterventionPanel(): void {
        this.panelService.setPanel(ActionsEnum.FIELD_INTERVENTION);
        this.contentLayoutService.changeContentSize(ActionsEnum.FIELD_INTERVENTION);
    }

    private setPendingFieldInterventionArea(fieldInterventionArea: MultiPolygon | null): void {
        if (!this.pendingFieldInterventionModalData || !fieldInterventionArea) {
            return;
        }

        this.pendingFieldInterventionModalData = {
            ...this.pendingFieldInterventionModalData,
            fieldInterventionArea,
        };
    }

    private splitFieldInterventionsByStatus(
        fieldInterventions: FieldIntervention[],
        searchValue: string,
    ): InterventionsByStatus[] {
        const normalizedSearchValue = searchValue.trim().toLowerCase();

        return fieldInterventions.reduce((acc: InterventionsByStatus[], intervention: FieldIntervention) => {
            if (!intervention.name.toLowerCase().includes(normalizedSearchValue)) {
                return acc;
            }

            const status = acc.find(
                (interventionByStatus: InterventionsByStatus) => interventionByStatus.name === intervention.status,
            );
            if (status) {
                status.fieldInterventions.push(intervention);

                return acc;
            }

            acc.push({ fieldInterventions: [intervention], name: intervention.status });

            return acc;
        }, []);
    }

    private startFieldInterventionAreaSelection(
        creationEditionForm: FieldInterventionCreationEditionForm | undefined,
    ): void {
        if (!creationEditionForm) {
            return;
        }

        this.pendingFieldInterventionModalData = this.buildPendingFieldInterventionModalData(creationEditionForm);
        this.listenFieldInterventionAreaSelectionConfirmation();
        this.fieldInterventionAreaSelectionModeService.open(FieldInterventionAreaSelectionContextEnum.CREATE_MODAL);
    }
}
